import React from 'react';

export interface TopicCount {
  /** The category title, or the uncategorised label. Also the filter value. */
  label: string;
  count: number;
}

interface TopicRailProps {
  topics: TopicCount[];
  /** Null means no filter — the "All" entry is selected. */
  selected: string | null;
  onSelect: (label: string | null) => void;
  /** Total across every topic, shown against "All". */
  total: number;
  allLabel?: string;
}

/**
 * The library's left rail: every topic, its tally, and which one you are in.
 *
 * This replaces a run of `GroupHeading` blocks down the page. Grouping by category
 * inline meant the only way to reach a topic was to scroll past the ones above it,
 * and with the whole active set loaded client-side that is a filter pretending to be
 * an outline. A rail states the shape of the library up front and gets you into a
 * topic in one click.
 *
 * Buttons, not links: the filter is view state, not an address. If topics should be
 * linkable and shareable they need to become a route or a query parameter, which is
 * a routing decision rather than a styling one.
 */
const TopicRail: React.FC<TopicRailProps> = ({
  topics,
  selected,
  onSelect,
  total,
  allLabel = 'All topics',
}) => {
  const row = (label: string, count: number, value: string | null): React.ReactNode => {
    const active = selected === value;
    return (
      <li key={label} className="border-b border-rule">
        <button
          type="button"
          onClick={() => onSelect(value)}
          aria-current={active ? 'true' : undefined}
          className={`flex w-full items-center justify-between gap-4 py-4 text-left font-ui text-step-0 transition-colors duration-fast ease-sheet ${
            active ? 'font-medium text-ink' : 'text-ink-muted hover:text-ink'
          }`}
        >
          <span className="min-w-0 truncate">{label}</span>
          <span className="shrink-0 font-ui text-step--2 font-medium tabular-nums tracking-[0.18em] text-ink-muted">
            {count}
            <span className="sr-only"> items</span>
          </span>
        </button>
      </li>
    );
  };

  return (
    // Sticky from `md` up, where the rail sits beside the grid rather than above it.
    // `top-8` matches the page's own top padding, so the rail doesn't sit flush against
    // the viewport edge while pinned. `self-start` is load-bearing, not optional: with
    // the row's default `align-items: stretch`, this box is stretched to match the
    // grid's full height, and a stretched box is exactly as tall as its own containing
    // block — leaving sticky nothing to travel within, so it never clamps and just
    // scrolls past the offset (confirmed empirically; this isn't a theoretical concern).
    // `self-start` shrinks the box back to the rail's own short content height, but the
    // row itself stays tall because of the grid sibling, so the rail still travels the
    // row's full height and releases at the bottom of the list rather than never at all.
    <aside className="w-full shrink-0 md:sticky md:top-8 md:w-64 md:self-start">
      {/* The One Label Rule's single label style, matching `GroupHeading` — this rail is
          the same kind of thing, a rule over a run of keyed rows. */}
      <h2 className="border-b border-rule-strong pb-2 font-ui text-step--2 font-medium uppercase tracking-[0.18em] text-ink-muted">
        Topics
      </h2>
      <ul className="mt-2">
        {row(allLabel, total, null)}
        {topics.map((topic) => row(topic.label, topic.count, topic.label))}
      </ul>
    </aside>
  );
};

export default TopicRail;
